import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FiX, FiUserCheck, FiUsers } from "react-icons/fi";
import { toast } from "react-toastify";
import { userRequest } from "../../utils/requestMethods";
import {
  assignClassLevelTeacher,
  extractApiErrorMessage,
  getPrimarySubjectTeacher,
  getActiveAssistantTeachers,
} from "../../utils/classGroupApi";
import { DEFAULT_ARM_LABEL, getTeacherName, pluralize } from "../../utils/classGroupHelpers";

const AssignClassTeacherModal = ({
  isOpen,
  onClose,
  schoolId,
  classGroupId,
  subject, // { _id, subject_name, subject_teacher, subject_teachers } or wrapped shape
  linkedClasses = [], // [{ class_id, class_name, group_label, teacher }] — this subject in each arm
  subclassLabel = DEFAULT_ARM_LABEL,
  teachersList = [],
  onSuccess,
}) => {
  // Normalize subject fields defensively
  const rawSubject = subject?.subject || subject;
  const subjectId = rawSubject?._id || rawSubject?.subject_id || rawSubject?.id;
  const subjectName = rawSubject?.subject_name || rawSubject?.name || "Subject";
  const groupTeacherId = getPrimarySubjectTeacher(rawSubject) || "";
  const currentAssistantIds = getActiveAssistantTeachers(rawSubject);
  const armLower = subclassLabel.toLowerCase();
  const armsPlural = pluralize(subclassLabel, 2).toLowerCase();

  const [role, setRole] = useState("primary");
  const [teachers, setTeachers] = useState(teachersList);
  const [selectedTeacherId, setSelectedTeacherId] = useState(groupTeacherId);
  const [loading, setLoading] = useState(false);
  const [fetchingTeachers, setFetchingTeachers] = useState(false);
  const [error, setError] = useState("");

  // Start from the current teacher (or nothing). Never pre-pick the first teacher in
  // the list — an admin could assign the wrong person just by clicking the button.
  useEffect(() => {
    if (!isOpen) return;
    setSelectedTeacherId(role === "assistant" ? "" : groupTeacherId);
  }, [isOpen, role, groupTeacherId]);

  useEffect(() => {
    if (teachersList && teachersList.length > 0) {
      setTeachers(teachersList);
      return;
    }

    const fetchTeachers = async () => {
      if (!schoolId) return;
      try {
        setFetchingTeachers(true);
        const { data } = await userRequest.get(`/teachers?school_id=${schoolId}&limit=200`);
        setTeachers(data.teachers || []);
      } catch (err) {
        console.error("Error fetching teachers:", err);
      } finally {
        setFetchingTeachers(false);
      }
    };

    if (isOpen) {
      fetchTeachers();
    }
  }, [schoolId, isOpen, teachersList]);

  if (!isOpen || !subject) return null;

  // The backend only gives the main teacher to arms that have no main teacher for this
  // subject yet — an arm that already has one (even the group's current teacher) keeps it.
  const armsWithTeacher = linkedClasses.filter((lc) => lc.teacher);
  const isChangingTeacher = Boolean(groupTeacherId) && selectedTeacherId !== groupTeacherId;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedTeacherId) {
      setError("Choose a teacher first");
      return;
    }
    if (!subjectId) {
      setError("Subject ID is missing. Please refresh and try again.");
      return;
    }

    try {
      setLoading(true);
      setError("");
      const res = await assignClassLevelTeacher(
        schoolId,
        classGroupId,
        subjectId,
        selectedTeacherId,
        role,
      );

      const appliedCount = res?.fanned_out_to?.length || 0;
      const skippedCount = res?.skipped_existing_overrides?.length || 0;
      const teacherName = getTeacherName(teachers, selectedTeacherId);
      const skippedNote =
        role === "assistant"
          ? "already had this teacher"
          : "kept their own teacher";

      toast.success(
        `${teacherName} is now ${role === "assistant" ? "an assistant" : "the teacher"} for ${subjectName} in ${appliedCount} ${pluralize(subclassLabel, appliedCount).toLowerCase()}.${
          skippedCount > 0
            ? ` ${skippedCount} ${pluralize(subclassLabel, skippedCount).toLowerCase()} ${skippedNote}.`
            : ""
        }`,
      );

      if (onSuccess) {
        onSuccess(res);
      }
      onClose();
    } catch (err) {
      console.error("Error assigning shared-subject teacher:", err);
      setError(extractApiErrorMessage(err, "Failed to assign the teacher"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl border border-slate-200 overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-indigo-50 text-indigo-600 rounded-lg">
              <FiUserCheck className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-slate-900">
                {role === "assistant" ? "Add an assistant teacher" : "Set the subject teacher"}
              </h3>
              <p className="text-xs text-slate-500">
                <span className="font-semibold text-slate-800">{subjectName}</span> — for every{" "}
                {armLower} in this group
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg hover:bg-slate-100"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto">
          {error && (
            <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 text-xs text-rose-700">
              {error}
            </div>
          )}

          <div>
            <span className="block text-sm font-medium text-slate-700">Role</span>
            <div className="mt-1.5 flex rounded-lg border border-slate-300 overflow-hidden">
              <button
                type="button"
                onClick={() => setRole("primary")}
                className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
                  role === "primary"
                    ? "bg-indigo-600 text-white"
                    : "bg-white text-slate-600 hover:bg-slate-50"
                }`}
              >
                Main teacher
              </button>
              <button
                type="button"
                onClick={() => setRole("assistant")}
                className={`flex-1 px-3 py-2 text-sm font-medium transition-colors border-l border-slate-300 ${
                  role === "assistant"
                    ? "bg-indigo-600 text-white"
                    : "bg-white text-slate-600 hover:bg-slate-50"
                }`}
              >
                Assistant
              </button>
            </div>
            <p className="mt-1 text-xs text-slate-500">
              {role === "primary"
                ? "The main teacher can manage this subject's tests and results for the group."
                : "Assistants help the main teacher. Adding one doesn't replace anyone."}
            </p>
          </div>

          {role === "assistant" && currentAssistantIds.length > 0 && (
            <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
              <p className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                <FiUsers className="w-3.5 h-3.5" /> Current assistants
              </p>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {currentAssistantIds.map((id) => (
                  <span
                    key={id}
                    className="px-2 py-0.5 rounded-md bg-white border border-slate-200 text-[11px] font-medium text-slate-700"
                  >
                    {getTeacherName(teachers, id)}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div>
            <label htmlFor="teacher_select" className="block text-sm font-medium text-slate-700">
              Teacher
            </label>
            {fetchingTeachers ? (
              <div className="py-2 text-xs text-slate-500">Loading teachers...</div>
            ) : (
              <select
                id="teacher_select"
                value={selectedTeacherId}
                onChange={(e) => {
                  setSelectedTeacherId(e.target.value);
                  if (error) setError("");
                }}
                className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
              >
                <option value="">-- Choose a teacher --</option>
                {teachers.map((t) => (
                  <option key={t._id} value={t._id}>
                    {getTeacherName(teachers, t._id)} {t.staff_id ? `(${t.staff_id})` : ""}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Who this affects — the main teacher only reaches arms with no teacher yet */}
          {role === "primary" && linkedClasses.length > 0 && (
            <div className="rounded-xl border border-slate-200 overflow-hidden">
              <p className="px-3.5 py-2 bg-slate-50 text-xs font-semibold text-slate-700 border-b border-slate-200">
                Who this affects
              </p>
              <ul className="divide-y divide-slate-100">
                {linkedClasses.map((lc) => (
                  <li
                    key={lc.class_id}
                    className="px-3.5 py-2 flex items-center justify-between gap-3 text-xs"
                  >
                    <span className="font-medium text-slate-800">{lc.class_name}</span>
                    {lc.teacher ? (
                      <span className="text-right text-slate-600">
                        Keeps {getTeacherName(teachers, lc.teacher)} ·{" "}
                        <Link
                          to={`/subjects/${lc.class_id}`}
                          className="font-semibold text-indigo-600 underline hover:text-indigo-800"
                        >
                          change it there
                        </Link>
                      </span>
                    ) : (
                      <span className="text-emerald-700">Gets this teacher</span>
                    )}
                  </li>
                ))}
              </ul>
              {armsWithTeacher.length > 0 && (
                <p
                  className={`px-3.5 py-2 text-[11px] border-t ${
                    isChangingTeacher
                      ? "bg-amber-50 text-amber-800 border-amber-100"
                      : "bg-slate-50 text-slate-600 border-slate-200"
                  }`}
                >
                  {isChangingTeacher
                    ? `Changing the teacher here doesn't move ${armsWithTeacher.length === linkedClasses.length ? `any ${armLower}` : `the ${armsWithTeacher.length} ${pluralize(subclassLabel, armsWithTeacher.length).toLowerCase()}`} that already ${armsWithTeacher.length === 1 ? "has" : "have"} a teacher — change ${armsWithTeacher.length === 1 ? "it" : "each one"} on its Subjects page.`
                    : `A ${armLower} that already has a teacher for this subject keeps them.`}{" "}
                  This doesn't change any {armLower}'s class teacher.
                </p>
              )}
            </div>
          )}

          {role === "primary" && linkedClasses.length === 0 && (
            <p className="text-xs text-slate-500">
              This teacher will be used for {subjectName} in all {armsPlural} of this group. It
              doesn't change any {armLower}'s class teacher.
            </p>
          )}

          <div className="mt-6 flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !selectedTeacherId}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-sm disabled:opacity-50 transition-colors flex items-center gap-2"
            >
              {loading && (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
              )}
              {role === "assistant" ? "Add assistant" : "Save teacher"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AssignClassTeacherModal;
